import { useCallback, useEffect, useState } from 'react'
import styled from '@emotion/styled'
import Client from 'fhirclient/lib/Client'
import { Actions, useAdministrationStateContext } from '../../components/state'
import queryString from 'query-string'
import moment from 'moment'
import { toAdministrationTimeString, toDatePickerMoment } from '../../utilities'
const fhirpath = require('fhirpath')

export type CdsPrefetchHandlerProps = {
    client: Client
    forceLatestAdministrationTime: boolean
    latestAdministrationTime: string | moment.Moment
}

const Content = styled.div`
`

export function CdsPrefetchHandler({ client, forceLatestAdministrationTime, latestAdministrationTime }: CdsPrefetchHandlerProps) {
    const { globalState: state, dispatch } = useAdministrationStateContext()
    const [isLoading, setIsLoading] = useState(false)

    const PopulatePrefetches = useCallback(() => {
        async function populate() {
            if (state.servicesLoaded && state.medicationPopulated && state.cdsServices && !state.prefetchLoaded) {
                const updatedServices = [...state.cdsServices]

                for (const service of updatedServices) {
                    if(service.prefetch){
                        const prefetch: { [k: string]: any } = {}
                        for (const key of Object.keys(service.prefetch)) {
                            let url = service.prefetch[key]
                            const expression = /\{{(.*?)\}}/g
                            const found = url.match(expression)

                            const context: { [k: string]: any } = {}
                            context['patientId'] = client.patient.id

                            if (client.encounter?.id) {
                                context['encounterId'] = client.encounter.id
                            }

                            context['effective-time'] = state.medicationRequest.dosageInstruction ?
                                state.medicationRequest.dosageInstruction.timing.event :
                                toAdministrationTimeString(moment())

                            for (const element of found) {
                                let newElement = element

                                for (const [key, value] of Object.entries(context)) {
                                    newElement = newElement
                                        .replace(`{{context.${key}}}`, value)
                                        .replace(`context.${key}`, `@${value}`)
                                }

                                newElement = newElement
                                    .replace('{{', '')
                                    .replace('}}', '')

                                const res = fhirpath.evaluate({}, newElement, {})
                                url = url.replace(
                                    `${element}`,
                                    res.length > 0 ?
                                        res :
                                        newElement
                                )
                            }
                            url = encodeUriParameters(url)

                            if ((key === 'Encounter' && client.encounter.id !== null) || key !== 'Encounter') {
                                await client
                                    .request(url, {
                                        resolveReferences: ['medicationReference'],
                                        pageLimit: 0
                                    })
                                    .then((response) => {
                                        if (Array.isArray(response)) {
                                            prefetch[key] = response[0]
                                        } else {
                                            prefetch[key] = response
                                        }
                                    })
                                    .catch((error) => {
                                        dispatch(Actions.AddError({ message: 'Error occured while fetching prefetch for cds service.', stacktrace: error.message }))
                                    })
                            }
                        }

                        service.populatedPrefetch = prefetch
                        if ((service.id === 'cumulative-dose-check' || service.id === 'cumulative-dose-chart-check') && forceLatestAdministrationTime) {
                            const latestEntriesList = []
                            for (const element of service.populatedPrefetch.Administrations.entry) {
                                const administration = { ...element }
                                delete administration.listId

                                latestEntriesList.push(administration)
                            }

                            const filtered = latestEntriesList.filter(x => toDatePickerMoment(x.resource.effectiveDateTime) < latestAdministrationTime)
                            if (service.populatedPrefetch?.Administrations?.entry) {
                                service.populatedPrefetch.Administrations.entry = filtered
                            }
                        }
                    } else {
                        dispatch(Actions.AddError({ 
                            message: `Prefetch for the service is empty`, 
                            stacktrace: [
                                service.url,
                                '\nPlease make sure services in CDS Services panel are available'
                            ]
                        }))
                    }
                }

                dispatch(Actions.SetCdsServices(updatedServices))
                dispatch(Actions.SetPrefetchLoaded(true))
            }

            setIsLoading(false)
        }

        if (!isLoading) {
            setIsLoading(true)
            populate()
        }
    // eslint-disable-next-line
    }, [isLoading,
        state.servicesLoaded,
        state.medicationPopulated,
        state.cdsServices,
        state.prefetchLoaded,
        state.administrations,
        state.medicationRequest.dosageInstruction,
        dispatch,
        forceLatestAdministrationTime,
        client,
        latestAdministrationTime
    ])

    useEffect(() => {
        PopulatePrefetches()
    }, [state.cdsServices,
    state.servicesLoaded,
    state.medicationPopulated,
    state.prefetchLoaded,
        PopulatePrefetches
    ])

    function encodeUriParameters(template: string) {
        if (template && template.split('?').length > 1) {
            const splitUrl = template.split('?')
            const queryParams = queryString.parse(splitUrl[1])
            Object.keys(queryParams).forEach((param) => {
                const val = queryParams[param] as string
                queryParams[param] = encodeURIComponent(val)
            })
            splitUrl[1] = queryString.stringify(queryParams, { encode: false })
            return splitUrl.join('?')
        }

        return template
    }

    return <Content />
}
